// src/components/Branding.js
import React, { useEffect } from "react";
import { Text, InputToggle, ActionButton } from "../custom-components";
import "../styles/branding.css";

const DEFAULT_LIGHT_COLORS = {
  cardBackground: "#FFFFFF",
  cardFont: "#111827",
  buttonBackground: "#0F3CC9",
  buttonFont: "#FFFFFF",
  linkFont: "#0A2885",
};

const DEFAULT_DARK_COLORS = {
  cardBackground: "#071026",
  cardFont: "#E6EEF5",
  buttonBackground: "#0F3CC9",
  buttonFont: "#FFFFFF",
  linkFont: "#6AB0FF",
};

export default function BrandingTab({
  colors,
  setColors,
  darkColors,
  setDarkColors,
  darkMode,
  setDarkMode,
  consentBanner,
  setConsentBanner,
  declineBtn,
  setDeclineBtn,
  managePrefsBtn,
  setManagePrefsBtn,
  acceptAllBtn,
  setAcceptAllBtn,
  acceptNecessaryBtn,
  setAcceptNecessaryBtn,
  onOpenManagePreferences,
  activePreview,
  setActivePreview,
  placement,
  setPlacement,

  // 👇 preferences states are now received from parent
  prefColors,
  setPrefColors,
  prefDarkColors,
  setPrefDarkColors,
  prefDarkMode,
  setPrefDarkMode,
}) {
  useEffect(() => {
    onOpenManagePreferences?.(false);
    setActivePreview?.("cookie");
  }, [onOpenManagePreferences, setActivePreview]);

  // 💾 Persist cookie tab colors
  useEffect(() => {
    sessionStorage.setItem("brandingColors", JSON.stringify(colors));
    sessionStorage.setItem("brandingDarkColors", JSON.stringify(darkColors));
  }, [colors, darkColors]);

  // 💾 Persist preferences tab colors
  useEffect(() => {
    sessionStorage.setItem("brandingPrefColors", JSON.stringify(prefColors));
    sessionStorage.setItem("brandingPrefDarkColors", JSON.stringify(prefDarkColors));
    sessionStorage.setItem("brandingPrefDarkMode", JSON.stringify(prefDarkMode));
  }, [prefColors, prefDarkColors, prefDarkMode]);

  // 🧹 Reset everything
  const handleCancel = () => {
    setColors({ ...DEFAULT_LIGHT_COLORS });
    setDarkColors({ ...DEFAULT_DARK_COLORS });
    setPrefColors({ ...DEFAULT_LIGHT_COLORS });
    setPrefDarkColors({ ...DEFAULT_DARK_COLORS });
    setDarkMode(false);
    setPrefDarkMode(false);
    setConsentBanner(true);
    setDeclineBtn(true);
    setManagePrefsBtn(true);
    setAcceptAllBtn(true);
    setAcceptNecessaryBtn(true);
    sessionStorage.clear();
  };

  // 💾 Save explicitly
  const handleSave = () => {
    sessionStorage.setItem("brandingColors", JSON.stringify(colors));
    sessionStorage.setItem("brandingDarkColors", JSON.stringify(darkColors));
    sessionStorage.setItem("brandingPrefColors", JSON.stringify(prefColors));
    sessionStorage.setItem("brandingPrefDarkColors", JSON.stringify(prefDarkColors));
    sessionStorage.setItem("brandingPrefDarkMode", JSON.stringify(prefDarkMode));

  };

  const colorInput = (label, value, onChange) => (
    <div style={{ marginBottom: 12 }}>
      <Text appearance="body-xs" color="primary-grey-80">
        {label}
      </Text>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="color"
          value={value}
          onChange={onChange}
          style={{ width: 36, height: 36, border: "none", cursor: "pointer" }}
        />
        <input
          type="text"
          value={value}
          readOnly
          style={{
            flex: 1,
            height: 40,
            borderRadius: 8,
            border: "1px solid #E5E7EB",
            padding: "8px 12px",
            background: "#fff",
            fontSize: 13,
          }}
        />
      </div>
    </div>
  );

  const ColorThemeSection = ({ isDark, setDark, light, setLight, dark, setDarkColors }) => (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
        <div>
          {colorInput("Card background", light.cardBackground, (e) =>
            setLight((p) => ({ ...p, cardBackground: e.target.value }))
          )}
          {colorInput("Button background", light.buttonBackground, (e) =>
            setLight((p) => ({ ...p, buttonBackground: e.target.value }))
          )}
          {colorInput("Link font", light.linkFont, (e) =>
            setLight((p) => ({ ...p, linkFont: e.target.value }))
          )}
        </div>
        <div>
          {colorInput("Card font", light.cardFont, (e) =>
            setLight((p) => ({ ...p, cardFont: e.target.value }))
          )}
          {colorInput("Button font", light.buttonFont, (e) =>
            setLight((p) => ({ ...p, buttonFont: e.target.value }))
          )}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Text appearance="heading-xxs" color="primary-grey-80">
          Theme (Dark mode)
        </Text>
        <InputToggle
          checked={!!isDark}
          onChange={() => setDark((p) => !p)}
          size="medium"
          type="toggle"
        />
      </div>

      {isDark && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }}>
          <div>
            {colorInput("Card background (Dark)", dark.cardBackground, (e) =>
              setDarkColors((p) => ({ ...p, cardBackground: e.target.value }))
            )}
            {colorInput("Button background (Dark)", dark.buttonBackground, (e) =>
              setDarkColors((p) => ({ ...p, buttonBackground: e.target.value }))
            )}
            {colorInput("Link font (Dark)", dark.linkFont, (e) =>
              setDarkColors((p) => ({ ...p, linkFont: e.target.value }))
            )}
          </div>
          <div>
            {colorInput("Card font (Dark)", dark.cardFont, (e) =>
              setDarkColors((p) => ({ ...p, cardFont: e.target.value }))
            )}
            {colorInput("Button font (Dark)", dark.buttonFont, (e) =>
              setDarkColors((p) => ({ ...p, buttonFont: e.target.value }))
            )}
          </div>
        </div>
      )}
    </>
  );

  const setLightBoth = (updater) => {
    setColors(updater);
    setPrefColors(updater);
  };
  const setDarkColorsBoth = (updater) => {
    setDarkColors(updater);
    setPrefDarkColors(updater);
  };
  const setDarkModeBoth = (updater) => {
    setDarkMode(updater);
    setPrefDarkMode(updater);
  };

  return (
    <div style={{ padding: 20, background: "#fff", borderRadius: 12 }}>
      <div style={{ marginBottom: 20 }}>
        <Text appearance="heading-xxs" color="primary-grey-80">
          Cookie banner appearance
        </Text>
        <div style={{ display: "flex", gap: 16, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
          {[
            { label: "Banner", value: "banner" },
            { label: "Modal", value: "modal" },
            { label: "Tool-tip", value: "tooltip" },
          ].map((opt) => (
            <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="radio"
                name="cookie-placement"
                checked={placement === opt.value}
                onChange={() => setPlacement(opt.value)}
              />
              <Text appearance="body-s" color="primary-grey-80">{opt.label}</Text>
            </label>
          ))}
        </div>
      </div>

      <Text appearance="heading-xxs" color="primary-grey-80">
        Consent modal look and feel
      </Text>
      <div style={{ marginTop: 12 }}>
        <ColorThemeSection
          isDark={darkMode}
          setDark={setDarkModeBoth}
          light={colors}
          setLight={setLightBoth}
          dark={darkColors}
          setDarkColors={setDarkColorsBoth}
        />
      </div>

      <Text appearance="heading-xxs" color="primary-grey-80" style={{ marginTop: 20 }}>
        Banner buttons
      </Text>
      {[
        { label: "Decline button", state: declineBtn, setter: setDeclineBtn },
        { label: "Accept all button", state: acceptAllBtn, setter: setAcceptAllBtn },
        { label: "Accept necessary button", state: acceptNecessaryBtn, setter: setAcceptNecessaryBtn },
        { label: "Manage preferences button", state: managePrefsBtn, setter: setManagePrefsBtn },
        { label: "Enable consent banner", state: consentBanner, setter: setConsentBanner },
      ].map((opt) => (
        <div key={opt.label} style={{ marginTop: 14 }}>
          <Text appearance="body-s" color="primary-grey-80">{opt.label}?</Text>
          <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="radio" checked={opt.state === true} onChange={() => opt.setter(true)} />
              <span style={{ color: "#0A3CCE", fontWeight: 600 }}>Yes</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="radio" checked={opt.state === false} onChange={() => opt.setter(false)} />
              <span>No</span>
            </label>
          </div>
        </div>
      ))}

      {/* Footer */}
      <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
        <ActionButton kind="primary" size="small" label="Save" onClick={handleSave} />
        <ActionButton kind="secondary" size="small" label="Cancel" onClick={handleCancel} />
      </div>
    </div>
  );
}
